import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';

import * as service from '../../services/canvaAuth';

interface AuthorizeResponse {
  code: string;
  error?: string;
  error_description?: string;
}

interface OAuthError extends Error {
  code?: string;
  description?: string;
}

let params: any;

// Helper function to get user-friendly error messages
export const getOAuthErrorMessage = (errorCode: string): string => {
  const errorMessages: Record<string, string> = {
    invalid_request:
      'Invalid authorization request. The request parameters are incorrect or missing.',
    access_denied: "Looks like you don't have access",
    unknown_error: 'Something when wrong',
  };

  return errorMessages[errorCode] || `Authorization error: ${errorCode}`;
};

export const getOauthErrorHelpMessage = (errorCode: string): string => {
  const errorMessages: Record<string, string> = {
    access_denied:
      'Only authorised users in your organisation have access to The Martec app in Canva',
  };

  return errorMessages[errorCode] || '';
};

export const useCanvaAuthorize = () => {
  const token = useSelector((store: any) => store?.auth?.token);

  const query = useQuery<AuthorizeResponse, OAuthError>({
    queryKey: ['canvaAuthorize', token],
    queryFn: async () => {
      const urlSearchParams = new URLSearchParams(window.location.search);
      params = Object.fromEntries(urlSearchParams.entries());

      const response: AuthorizeResponse = await service.createCanvaAuthorize(
        params,
        token,
      );

      // Handle OAuth error responses
      if (response.error) {
        const error = new Error(
          getOAuthErrorMessage(response.error) || response.error_description,
        ) as OAuthError;
        error.code = response.error || 'unknown_error';
        throw error;
      }

      // Pass to success
      return response;
    },
    retry: false,
    useErrorBoundary: false,
    refetchOnWindowFocus: false,
    enabled: !!token && token != undefined, // Only run if token exists
    onSuccess: (data: AuthorizeResponse) => {
      const encodeCode = encodeURIComponent(data.code);
      const encodeState = encodeURIComponent(params.state);

      const redirectLocation = `${params.redirect_uri}?code=${encodeCode}&state=${encodeState}`;

      window.location.replace(redirectLocation);
    },
    onError: (error: OAuthError) => {
      console.error('[useCanvaAuthorize:onError] Authorization failed:', {
        message: error.message,
        code: error.code,
        description: error.description,
      });
    },
  });

  return query;
};
